import type { ReactNode, JSX } from 'react'

import { Button, CoachMark, Flex } from '@buffer-mono/popcorn'

/**
 * How prominently a channel row is pointed at. `card` also shows the
 * explanation; `spotlight` is only the pulsing ring, so a page full of
 * affected channels doesn't stack overlapping popovers.
 */
export type LinkFacebookPageHint = 'card' | 'spotlight'

type LinkFacebookPageCoachmarkProps = {
  hint?: LinkFacebookPageHint
  onDismiss: () => void
  children: ReactNode
}

/**
 * Points an actor arriving from the Community mentions notice at the channel
 * menu holding "Link Account to Facebook Page".
 *
 * Renders `children` untouched when there's no hint, so rows that don't need
 * linking are unaffected.
 */
export const LinkFacebookPageCoachmark = ({
  hint,
  onDismiss,
  children,
}: LinkFacebookPageCoachmarkProps): JSX.Element => {
  if (!hint) {
    return <>{children}</>
  }

  return (
    <CoachMark open dismissTimeout={null} onDismiss={onDismiss}>
      <CoachMark.Overlay>
        {children}
        <CoachMark.Spotlight />
      </CoachMark.Overlay>
      {hint === 'card' && (
        <CoachMark.Content side="left" align="center" showCloseIcon={false}>
          <Flex direction="column" gap="md" align="stretch">
            <Flex direction="column" gap="xs">
              <CoachMark.Title>Link Account to Facebook Page</CoachMark.Title>
              <CoachMark.Description>
                To access Instagram mentions, link your Instagram account to a
                Facebook Page.
              </CoachMark.Description>
            </Flex>
            <CoachMark.Footer>
              <CoachMark.Dismiss>
                <Button variant="primary">Got It</Button>
              </CoachMark.Dismiss>
            </CoachMark.Footer>
          </Flex>
        </CoachMark.Content>
      )}
    </CoachMark>
  )
}
